// Recipients Management Module - TypeScript Interfaces

import type { MailboxGone } from '@/lib/recipients/mailbox-gone'

// ============ FIELD OPERATORS ============
export type FieldOperator =
  | 'is'
  | 'is_not'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'is_empty'
  | 'is_not_empty'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'before_date'
  | 'after_date'
  | 'in_last_days'
  | 'in_list'
  | 'not_in_list'

// ============ RECIPIENT ============
export type RecipientStatus = 'Active' | 'Inactive' | 'Bounced' | 'Unsubscribed'

// Source tracking - where the recipient was imported from.
// Mirrors the backend `recipient_source_type` pgEnum (Title Case With Spaces,
// acronyms upper) so the BE response narrows cleanly into this union.
// Legacy UPPER_SNAKE_CASE shapes (`GOOGLE_WORKSPACE`, `MICROSOFT_O365`, `OKTA`)
// are kept in `RECIPIENT_SOURCE_TYPE_LEGACY` below for migration / mock data.
export type RecipientSourceType =
  | 'Manual'
  | 'CSV'
  | 'Google Workspace'
  | 'Microsoft O365'
  | 'LDAP'
  | 'Okta'
  | 'AD Syncer'
  | 'HFv2'
  | 'SCIM'
  | 'Unknown'

export interface RecipientSource {
  type: RecipientSourceType
  importId?: string           // FE-only — derived from sourceImportId, may be absent in some flows
  syncConfigId?: string       // FE-only — derived from sourceSyncConfigId
  externalId?: string         // FE-only — derived from sourceExternalId
  externalGroups?: string[]   // FE-only — derived from sourceExternalGroups
  importedAt: string          // backend sentinel '' when missing
  lastSyncedAt?: string       // FE-only — derived; may be absent
}

export interface Recipient {
  id: string
  email: string
  firstName: string
  lastName: string
  displayName?: string
  phoneNumber: string         // backend sentinel '' when empty
  department: string          // backend sentinel '' when empty
  designation: string         // backend sentinel '' when empty
  location: string            // backend sentinel '' when empty
  country: string             // backend sentinel '' when empty
  managerEmail?: string
  managerId?: string
  /**
   * #257 — the workspace DOMAIN this recipient is partitioned into (the MSSP
   * `recipients.domain_id` FK). This is what the Domain FILTER matches; both
   * fields are returned by the API (`recipient-response.dto.ts:99, :140`) and
   * were simply undeclared here, which is the
   * declaration-that-lies-by-omission class `tsc` cannot see. Empty string is
   * the backend's sentinel for "no linked domain".
   */
  domainId?: string
  /**
   * Label for `domainId`. NOT independent evidence of a link: the column is a
   * denormalised copy of the recipient's own email host on every write path,
   * so it is only meaningful when `domainId` is set.
   */
  domainName?: string
  /** Resolved manager first name — returned by list + detail endpoints via subquery. */
  managerFirstName?: string
  /** Resolved manager last name — returned by list + detail endpoints via subquery. */
  managerLastName?: string
  /**
   * Number of DISTINCT courses this recipient is enrolled in across all
   * statuses (Not Started + In Progress + Completed). Powers the "Courses"
   * column on the recipients list page. Returned by the list endpoint
   * via a correlated COUNT subquery; defaults to 0 elsewhere.
   */
  courseCount?: number
  /**
   * Pre-concatenated `"FirstName LastName"` — returned by the detail endpoint
   * (BE commit 9958e984). Prefer this over manual concat on the FE so display
   * stays consistent with the BE's name-resolution logic (handles missing
   * first/last gracefully, trims, falls back to email).
   */
  managerName?: string
  timezone?: string
  isRemote?: boolean
  hasPortalAccess?: boolean
  externalId?: string
  employeeId?: string
  upn?: string
  dateOfBirth?: string        // ISO date string YYYY-MM-DD (backend sentinel '' when empty)
  address?: string
  city?: string
  state?: string
  postalCode?: string
  locale?: string
  status: RecipientStatus
  /**
   * Why the person is Inactive when the provider said their mailbox no longer exists (spec
   * 2026-10-29-mailbox-gone-design.md): which provider, which feature found out, when, and the short
   * code. `null` for everyone else. Read off the wire by `parseMailboxGone` (lib/recipients/mailbox-gone.ts);
   * an admin setting the person Active clears it on the server.
   */
  mailboxGone?: MailboxGone | null
  tags: string[]
  // Wave-3 item 2 — a Multi Select value is a list (the API stores and
  // accepts one, R17), so the declared value type says so.
  customFields: Record<string, CustomFieldValue>
  lists: string[] // Many-to-many: recipient belongs to multiple lists
  source?: RecipientSource    // FE-only composite — backend exposes sourceType + sourceImportId etc. flat. Never built from the API: read sourceType / sourceExternalGroups (FE19)
  sourceType: RecipientSourceType // backend always returns sourceType (unrecognized stored values become 'Unknown')
  sourceExternalGroups?: string[] // backend sends [] when none — the directory groups the sync saw (FE19)
  personLinkCount: number     // FE-derived count; backend Person join — defaults to 0 in transformer ctx
  createdAt: string
  updatedAt: string
  createdBy: string
}

// ============ RECIPIENT LIST ============
export type ListType = 'Static' | 'Dynamic'

// Source tracking for groups - how the group was created
// FE-only composite — backend RecipientGroupResponseDto exposes sourceType / sourceSyncConfigId /
// sourceExternalGroupId as flat string fields (sentinel '' when manual).
export interface RecipientListSource {
  type: RecipientSourceType
  syncConfigId?: string       // FE-only — derived from sourceSyncConfigId
  externalGroupId?: string    // FE-only — derived from sourceExternalGroupId
  createdAt: string
}

export interface RecipientList {
  id: string
  name: string
  description: string         // backend sentinel '' when empty
  type: ListType
  recipientCount: number      // backend memberCount — sentinel 0
  rules?: DynamicListRule     // FE-only — backend uses filterExpression: {} on DTO
  // `staticRecipientIds?: string[]` DELETED 2026-08-10 (#238). It was declared
  // "FE-only — backend has no equivalent field", and no API has ever emitted it
  // (zero hits in apps/api/src), so every reader got `undefined` and every
  // membership diff computed against `[]`. The Edit Group drawer's remove call
  // was therefore unreachable and unticking a member did nothing. Member ids now
  // come from `GET /v1/recipients/groups/:id/member-ids` (useGroupMemberIds).
  // Do NOT re-add it — an optional field that is always undefined is invisible
  // to tsc and reads as data.
  isDefault: boolean
  color: string               // backend sentinel '' when empty
  source?: RecipientListSource // FE-only composite (see RecipientListSource note). Never built from the API: read sourceType via groupSourceType() (FE19)
  /** backend RecipientGroupResponseDto.sourceType — '' for a hand-made group, the provider for a sync-created one */
  sourceType?: string
  /** backend RecipientGroupResponseDto.lastRefreshedAt — '' when never refreshed. Read via groupLastRefreshedAt() (FE19) */
  lastRefreshedAt?: string
  createdAt: string
  updatedAt: string
  createdBy: string           // FE-derived from createdById
  lastSyncedAt: string        // backend lastRefreshedAt — sentinel '' when never synced
  /**
   * backend syncStatus — sentinel ''. 'Refresh failed' when a dynamic group's
   * last refresh failed permanently (GR10; lib/recipients/group-refresh-status).
   * Optional only because demo/mock rows predate it; the API always sends it.
   */
  syncStatus?: string
  /**
   * backend RecipientGroupResponseDto.categoryId (lane B2) — the group's
   * category, null when uncategorised. Optional only because demo/mock rows
   * predate it; the API always sends it.
   */
  categoryId?: string | null
}

// ============ DYNAMIC LIST RULES ============
export interface RuleCondition {
  id: string
  field: string // Field name or custom field key
  operator: FieldOperator
  value: string | number | boolean | Date | string[] // Array for 'in_list'
  valueEnd?: string | number | Date // For 'between' operator
  /**
   * The specific campaign / course id an entity-scoped condition targets
   * (field `campaign_outcome` / `course_status`) — e.g. "Status of <this
   * course> is Completed". Undefined for all non-entity fields.
   */
  entityId?: string
  /**
   * Days a windowable catalog field looks back (vCRO wave 3: `riskChange`,
   * `realThreatsReported`, `dataEntryCompromises`; whole days 7..365).
   * Undefined = the field's own default, which the API applies.
   */
  windowDays?: number
}

export interface RuleGroup {
  id: string
  logic: 'AND' | 'OR'
  conditions: (RuleCondition | RuleGroup)[]
}

export interface DynamicListRule {
  rootGroup: RuleGroup
  includeInactive: boolean
  excludeBouncedEmails: boolean
  excludeSuppressedRecipients: boolean
}

// ============ CUSTOM FIELDS ============
// Aligned with the backend `custom_field_type` pgEnum (Title Case With Spaces,
// acronyms upper). This union mirrors the 12 canonical BE values exactly — no
// drift (per hfv3-enum-convention: "A single enum is either one or the other —
// never both").
export type FieldType =
  | 'Text'
  | 'Number'
  | 'Date'
  | 'Boolean'
  | 'Select'
  | 'Multi Select'
  | 'Email'
  | 'URL'
  | 'Phone'
  | 'Currency'
  | 'Percentage'
  | 'Recipient Lookup'

/**
 * A stored custom-field value. A Multi Select holds a list of its options
 * (custom-field-values.ts R17 accepts a list or a single option); every other
 * type holds a scalar.
 */
export type CustomFieldValue = string | number | boolean | Date | string[]

export interface CustomField {
  id: string
  name: string
  key: string // Programmatic key (e.g., 'hire_date')
  type: FieldType
  description: string         // backend sentinel '' when empty
  isRequired: boolean
  options: string[]           // backend sentinel [] (forced [] for non-select types)
  defaultValue: string | number | boolean // backend always coerces to string sentinel '' — wider FE union retained for builder UI
  isActive: boolean
  isPromoted: boolean         // backend sentinel false
  promotedColumnName: string  // backend sentinel '' when not promoted
  sortOrder: number           // backend sentinel 0
  createdAt: string
  updatedAt: string
  recipientCount: number // Number of recipients using this field (backend usageCount, sentinel 0)
  /**
   * backend CustomFieldResponseDto.isReportable — offered as a report
   * breakdown and filter. Only Text, Select, Multi Select and Boolean may be
   * (lib/population/types.ts REPORTABLE_FIELD_TYPES). Optional only because
   * fixtures and demo rows predate it; the API always sends it.
   */
  isReportable?: boolean
}

// ============ CSV IMPORT ============
// Mirrors the backend `import_status` pgEnum + RecipientImportResponseDto.
// Title Case With Spaces. Last value used to be `Rolled_Back` (underscore) —
// kept as a literal alias only to avoid breaking older serialized data.
export type ImportStatus =
  | 'Pending'
  | 'Analyzing'
  | 'Ready'
  | 'Processing'
  | 'Completed'
  | 'Failed'
  | 'Partial'
  | 'Cancelled'
  | 'Rolled Back'
  // #288/#289/#312, #830 — some of the import's rows wait for a person in
  // Pending Actions (conflicts, held rows). NOT a failure: when the last one is
  // resolved it becomes 'Completed', or 'Partial' if some rows could not be
  // read. Added by migration 0109; 'Failed' = no row applied and some unreadable.
  | 'Needs Review'

/** Whether an import entry stopped its row ('error') or not ('warning'). */
export type ImportIssueSeverity = 'error' | 'warning'

/**
 * One entry of an import's `errors`, as GET /recipients/import/history and
 * /:id/status send it: the API's import response DTO normalises every stored
 * entry into this shape (recipient-import-response.dto.ts).
 */
export interface ImportError {
  /**
   * Zero-based data row of the uploaded file (shown as row + 1). An entry
   * about the whole import (no domain, a failed pipeline, a lost job) has no
   * row: it arrives as null or as -1. Read it through importIssueRowNumber(),
   * never as `row + 1`, so it renders as no row rather than "row 1" or
   * "row 0" (audit 2026-09-25, SI7).
   */
  row: number | null
  /** The recipient field, or '' for an entry about the whole import. */
  field: string
  message: string
  /** The value as the file had it; '' when there is none. */
  value: string
  /**
   * 'warning' for a row that WAS imported (a phone number kept as typed,
   * locale audit INPUT-06). The API always sends it; mock and older records
   * without it read as errors.
   */
  severity?: ImportIssueSeverity
}

export interface FieldMapping {
  csvColumn: string
  recipientField: string // System field or custom field key
  isCustomField: boolean
}

/**
 * #830 Part 2 — what the import wizard did with every row of the file it did
 * not send (the API's RecipientImportFileSummaryDto). With it:
 *   rowsInFile = totalRows (the rows sent) + removedBeforeImport
 *                + unreadableInWizard + duplicatesSkippedInWizard,
 * and unreadableInWizard is also part of errorCount.
 */
export interface ImportFileSummary {
  rowsInFile: number
  removedBeforeImport: number
  unreadableInWizard: number
  duplicatesSkippedInWizard: number
}

export interface ImportHistory {
  id: string
  fileName: string
  fileSize: number // in bytes
  status: ImportStatus
  totalRows: number
  processedRows: number
  successCount: number
  errorCount: number
  duplicateCount: number
  skipCount: number
  /**
   * #830 — this import's staging rows still waiting in Pending Actions
   * (resolution 'Pending'). The API counts it per import; normalizeImport()
   * defaults it to 0.
   */
  pendingReviewCount: number
  /**
   * #830 Part 2 — null for imports before the API stored it, AD Syncer runs
   * and uploads without a summary: those screens show what they always did.
   * normalizeImport() turns anything that is not four whole numbers into null.
   */
  fileSummary: ImportFileSummary | null
  canUndo: boolean
  undoneAt: string            // backend sentinel '' when not undone
  undoneById: string          // backend sentinel '' when not undone
  errors: ImportError[]       // backend sends the first 100 stored errors
  errorsTotal?: number        // backend: how many are stored (demo fixtures omit it)
  fieldMappings: FieldMapping[]
  targetGroupIds: string[] // Group IDs to add imported recipients to
  createdAt: string
  completedAt: string         // backend sentinel '' when not completed
  createdById: string // UUID of the user who created the import
  createdByName: string       // backend sentinel '' when not resolved
  createdByEmail?: string     // FE-only — not in backend DTO
  // Aliases used by legacy/route code (FE-only — backend uses successRows/errorRows/duplicateRows)
  successfulRows?: number     // FE-only legacy alias
  failedRows?: number         // FE-only legacy alias
  duplicateRows?: number      // FE-only legacy alias
  targetLists?: string[]      // FE-only legacy alias for targetGroupIds
  createdBy?: string          // FE-only legacy alias for createdById
}

// ============ CSV WIZARD STATE ============
export interface CSVWizardState {
  step: 1 | 2 | 3 | 4
  file: File | null
  parsedData: Record<string, string>[]
  csvHeaders: string[]
  fieldMappings: FieldMapping[]
  selectedLists: string[]
  tags: string[]
  updateExisting: boolean
  skipDuplicates: boolean
  previewData: Recipient[]
  validationErrors: ImportError[]
}

// ============ PROTECTABLE FIELDS (for conflict resolution) ============
export const PROTECTABLE_FIELDS = [
  { id: 'name', label: 'Name' },
  { id: 'firstName', label: 'First Name' },
  { id: 'lastName', label: 'Last Name' },
  { id: 'department', label: 'Department' },
  { id: 'jobTitle', label: 'Job Title' },
  { id: 'phone', label: 'Phone' },
  { id: 'location', label: 'Location' },
  // A directory sync writes `status` on every update, so a manual
  // deactivation (on leave, opted out, excluded from training for a reason the
  // directory knows nothing about) is reversed by the next scheduled run while
  // the person is still listed upstream. Protecting it makes that decision
  // stick. Opt-in: a config that does not list it behaves exactly as before.
  { id: 'status', label: 'Status' },
] as const

export type ProtectableFieldId = typeof PROTECTABLE_FIELDS[number]['id']

// ============ DASHBOARD METRICS ============
export interface RecipientsMetrics {
  totalRecipients: number
  activeRecipients: number
  inactiveRecipients: number
  bouncedRecipients: number
  unsubscribedRecipients: number
  remoteRecipients: number
  totalGroups: number
  staticGroups: number
  dynamicGroups: number
  recentImports: number
  recipientsByDepartment: { department: string; count: number }[]
  recipientsByLocation: { location: string; count: number }[]
}

// ============ STAGING / CONFLICT RESOLUTION ============
// Mirrors the backend StagingRowResponseDto wire format:
//   sourceType uses 'Microsoft 365' (display label), not 'Microsoft O365' —
//     the staging DTO is the only recipients endpoint that normalizes the
//     provider label this way (matches the integrations sync DTO).
//   status is Title Case (the staging_status pgEnum was migrated to
//     Title-Case in W5).
export type StagingRowSourceType =
  | 'CSV'
  | 'Microsoft 365'
  | 'Google Workspace'
  | 'LDAP'
  | 'Okta'
  | 'AD Syncer'
  | 'HFv2'
  | 'Unknown'

export type StagingRowStatus =
  | 'New'
  | 'Duplicate'
  | 'Conflict'
  | 'Invalid'
  | 'Applied'
  | 'Skipped'
  | 'Rejected'

export interface StagingRow {
  id: string
  sourceType: StagingRowSourceType
  batchId: string
  status: StagingRowStatus
  resolution: string | null
  incomingData: Record<string, unknown> | null
  incomingEmail: string | null
  matchedRecipientId: string | null
  matchFields: Record<string, boolean | null> | null
  conflictDetails: { field: string; incoming: string; existing: string }[] | null
  existingRecipient?: Record<string, unknown> | null
  /**
   * #773 — why an Approve of this row would be refused (a code such as
   * `staging_row_email_required`), or null when it would not be. Sent for rows
   * still Pending; read through components/recipients/staging/approve-refusal.ts.
   */
  approveRefusal?: string | null
  /**
   * #773 — the incoming External ID ("" when none). A row with no email is
   * named by it in the list, its checkbox and the review dialog.
   */
  externalId?: string | null
  createdAt: string
}

export interface StagingListResponse {
  data: StagingRow[]
  total: number
  page: number
  limit: number
  pendingCount: number
}

// ============ SOURCE DISPLAY INFO ============
export const SOURCE_DISPLAY_INFO: Record<RecipientSourceType, { label: string; color: string }> = {
  Manual: { label: 'Manual', color: '#6b7280' },
  CSV: { label: 'CSV Import', color: '#3b82f6' },
  'Google Workspace': { label: 'Google Workspace', color: '#ea4335' },
  'Microsoft O365': { label: 'Microsoft 365', color: '#00a4ef' },
  LDAP: { label: 'LDAP/AD', color: '#f59e0b' },
  Okta: { label: 'Okta', color: '#007dc1' },
  'AD Syncer': { label: 'AD Syncer', color: '#9333ea' },
  HFv2: { label: 'HFv2', color: '#0f766e' },
  // A person the customer's identity platform provisions over SCIM 2.0 (identity integrations, 27 Oct 2026).
  SCIM: { label: 'SCIM', color: '#4f46e5' },
  Unknown: { label: 'Unknown', color: '#9ca3af' },
}

/**
 * #414 — label a source filter option the way the Source COLUMN labels it.
 *
 * The backend's filter-options endpoint returns the raw `recipient_source`
 * pgEnum array ('Microsoft O365' / 'CSV' / 'LDAP'). The recipients table used
 * to render those verbatim, so its own filter said "Microsoft O365" while the
 * column beside it said "Microsoft 365" — and seven other surfaces already used
 * the SOURCE_DISPLAY_INFO wording.
 *
 * Ticket-1460 pattern: the LABEL follows the product, the stored VALUE stays
 * raw. Swapping the value would filter on a string the API has never heard of.
 * An unknown source falls back to its raw value rather than being hidden — a
 * new enum member should still be selectable the day it ships.
 */
export function toSourceFilterOptions(
  raw: ReadonlyArray<string | { value: string; label: string }>,
): Array<{ value: string; label: string }> {
  return raw.map((entry) =>
    typeof entry === 'string'
      ? {
          value: entry,
          label:
            SOURCE_DISPLAY_INFO[entry as RecipientSourceType]?.label ?? entry,
        }
      : entry,
  );
}

/**
 * #414 — whether the Person column has anything to say for this tenant.
 *
 * `docs/architecture/2026-03-17-recipient-module-analysis.html` specifies this under
 * "UC1: Single Workspace, Single Domain": *"Workspace selector hidden (only 1)
 * / Domain filter hidden (only 1) / Person column hidden (1:1 mapping) /
 * Behaves exactly like current UI / Zero changes visible to UC1 users."*
 *
 * The Person entity links the same human's recipient rows ACROSS domains. With
 * one domain that mapping is 1:1 by construction, so the column can only ever
 * repeat itself — which is exactly what the reporter saw ("Single" on 100% of
 * rows). It is hidden there, not deleted: a second domain brings it back.
 *
 * An UNKNOWN count keeps the column. Absence of information is not a reason to
 * remove a column a multi-domain tenant depends on.
 */
export function showsPersonColumn(domainCount: number | undefined): boolean {
  if (domainCount === undefined) return true;
  return domainCount > 1;
}

// Legacy UPPER_SNAKE_CASE → canonical Title Case map. Useful when a consumer
// (mock data, onboarding wizard, query-string filter from older builds) still
// supplies the legacy shape and needs to be normalized before hitting the API.
export const RECIPIENT_SOURCE_TYPE_LEGACY: Record<string, RecipientSourceType> = {
  MANUAL: 'Manual',
  CSV: 'CSV',
  GOOGLE_WORKSPACE: 'Google Workspace',
  MICROSOFT_O365: 'Microsoft O365',
  MICROSOFT_365: 'Microsoft O365',
  LDAP: 'LDAP',
  OKTA: 'Okta',
  AD_SYNCER: 'AD Syncer',
  HFV2: 'HFv2',
  SCIM: 'SCIM',
  UNKNOWN: 'Unknown',
}

/** Coerce any RecipientSourceType-like input (legacy or canonical) to canonical. */
export function normalizeRecipientSourceType(value: unknown): RecipientSourceType {
  if (typeof value !== 'string') return 'Unknown'
  if (Object.prototype.hasOwnProperty.call(SOURCE_DISPLAY_INFO, value)) {
    return value as RecipientSourceType
  }
  return Object.prototype.hasOwnProperty.call(RECIPIENT_SOURCE_TYPE_LEGACY, value)
    ? RECIPIENT_SOURCE_TYPE_LEGACY[value]
    : 'Unknown'
}

/**
 * FE19 — where a group came from: the API's flat `sourceType` ('' for a
 * hand-made group, the provider name for one a directory sync created).
 */
export function groupSourceType(group: Pick<RecipientList, 'sourceType'>): RecipientSourceType {
  return group.sourceType ? normalizeRecipientSourceType(group.sourceType) : 'Manual'
}

/**
 * FE19 — when a dynamic group last refreshed. The API sends it as
 * `lastRefreshedAt` ('' when never); `lastSyncedAt` is the name the web type
 * declared (demo data still sets it) and the API never sends.
 */
export function groupLastRefreshedAt(
  group: Pick<RecipientList, 'lastRefreshedAt' | 'lastSyncedAt'>,
): string {
  return group.lastRefreshedAt || group.lastSyncedAt || ''
}

/**
 * FE19 — sources whose sync mirrors a person's identity fields: editing the
 * email of someone they own breaks the sync's match on the next pull. Manual
 * and CSV people are not re-pulled; an Unknown source is not known to be.
 */
const DIRECTORY_SYNC_SOURCES: ReadonlySet<RecipientSourceType> = new Set<RecipientSourceType>([
  'Google Workspace',
  'Microsoft O365',
  'LDAP',
  'Okta',
  'AD Syncer',
  'HFv2',
  // Mirrored from the identity platform, which matches on email or employee id: an edit here breaks that match.
  'SCIM',
])

export function isDirectorySyncedSource(sourceType: unknown): boolean {
  return DIRECTORY_SYNC_SOURCES.has(normalizeRecipientSourceType(sourceType))
}
